// @ts-check
import delve from 'dlv';
import log from 'loglevel';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { compose, graphql, withApollo } from 'react-apollo';

import { withConfig } from 'Context/ConfigContext';
import { nsUnregisterFcmToken } from 'GraphQL/mutation/FCMToken.mutation';
import { withCapacitor } from 'Util/Capacitors';
import { FCMPlugin } from 'Util/Capacitors/SkooldioCapacitor';

import { getStoreApolloClient } from '../../GraphQL';
import {
  enterClassroom,
  updateClassroomCurrentLearningMode,
  updateClassroomCurrentSubSection,
} from '../../GraphQL/mutation/Classroom.mutation';
import { classroomCourse } from '../../GraphQL/query/Classroom.query';
import useMyExternalProfile from '../../Routes/Classroom/Domain/useMyExternalProfile';
import { getClassroomProviderData, getError } from '../../Util/classroomProviderUtils';

import { LAYOUT_TYPE, LEARNING_MODE } from './constant';
import ClassroomContext from './context';

const getSubsectionsFromSections = (sections) => {
  const subsections = sections
    .map((section) =>
      section.subSections.map((subsection) => {
        return { ...subsection, sectionId: section?.id };
      })
    )
    .flat();
  return subsections;
};

const getLatestCompletedSubsection = (subsections, completedSubsections) => {
  const latestCompletedIndex = subsections
    .reverse()
    .findIndex(({ referId }) => completedSubsections.includes(referId));

  return subsections[latestCompletedIndex] || subsections[0];
};

const getCurrentSubsection = (
  subsections,
  referId,
  completedSubsections,
  subsectionReferId,
  targetSubsectionId
) => {
  if (targetSubsectionId && subsectionReferId) {
    const currentSubsection = subsections.find(
      (s) => String(s.id) === String(targetSubsectionId) && s.referId === subsectionReferId
    );
    if (currentSubsection) return currentSubsection;
  }

  if (subsectionReferId) {
    const currentSubsection = subsections.find(({ referId }) => subsectionReferId === referId);
    return currentSubsection
      ? currentSubsection
      : getLatestCompletedSubsection(subsections, completedSubsections);
  } else if (referId) {
    const currentSubsection = subsections.find(
      ({ referId: subsectionReferId }) => referId === subsectionReferId
    );
    return currentSubsection
      ? currentSubsection
      : getLatestCompletedSubsection(subsections, completedSubsections);
  } else {
    return subsections[0];
  }
};

const getToC = (data, isActiveSectionGroup = false) => {
  if (isActiveSectionGroup) {
    return delve(data, ['classroomCourse', 'tableOfContentWithSectionGroup'], []);
  } else {
    return delve(data, ['classroomCourse', 'tableOfContent'], []);
  }
};

const getSectionGroups = (data) => {
  return delve(data, ['sectionGroups'], []);
};

const getSections = (data, isActiveSectionGroup = false) => {
  if (isActiveSectionGroup) {
    const sectionGroups = delve(data, ['sectionGroups'], []);
    return sectionGroups.map((sectionGroup) => sectionGroup.sections).flat();
  }
  return delve(data, ['sections'], []);
};

const getCompletedSubsections = (data) =>
  delve(data, ['classroomStatus', 'subSectionStatus', 'subSectionStatus'], [])
    .filter((subSectionStatus) => subSectionStatus.status === 'COMPLETED')
    .map((subSectionStatus) => subSectionStatus.courseItemReferId);

const getFailedSubsections = (data) =>
  data?.classroomStatus?.subSectionStatus?.subSectionStatus
    .filter((subSectionStatus) => subSectionStatus.status === 'FAILED')
    .map((subSectionStatus) => subSectionStatus.courseItemReferId);

const getSubmittedSubsections = (data) =>
  data?.classroomStatus?.subSectionStatus?.subSectionStatus
    .filter((subSectionStatus) => subSectionStatus.status === 'SUBMITTED')
    .map((subSectionStatus) => subSectionStatus.courseItemReferId);

/** @returns {CourseData["learningModeCompletedSubsectionIds"] } */
const getLearningModeCompletedSubsections = (data) =>
  delve(data, ['classroomStatus', 'subSectionStatus', 'subSectionStatus'], [])
    .filter((subSectionStatus) => subSectionStatus.status === 'COMPLETED')
    .reduce((prev, subSectionStatus) => {
      const subsectionComplete =
        subSectionStatus.learningModes && subSectionStatus.learningModes.filter(Boolean).length > 0
          ? subSectionStatus.learningModes
          : [LEARNING_MODE.DEFAULT];
      return { ...prev, [subSectionStatus.courseItemReferId]: subsectionComplete };
    }, {});

const getLastWatchingSubsectionReferId = (data) =>
  delve(data, ['classroomStatus', 'currentProgress', 'currentCourseItemReferId'], null);

const ClassroomProvider = ({
  courseId,
  subsectionReferId,
  targetSubsectionId,
  isEnableVideoResume: isEnableVideoResumeProp,
  isEnableLearningMode,
  children,
  client,
  userProfile,
  updateClassroomCurrentSubSection,
  updateClassroomCurrentLearningMode,
  capacitorContext,
  appConfig: { native },
}) => {
  const [currentSubsectionId, setCurrentSubsectionId] = useState(null);
  const [currentSubsectionReferId, setCurrentSubsectionReferId] = useState(null);
  const [classroomCourseId, setClassroomCourseId] = useState(null);
  const [classroomTimeId, setClassroomTimeId] = useState(null);
  const [isEnableVideoResume, setIsEnableVideoResume] = useState(isEnableVideoResumeProp);
  const [layoutType, setLayoutType] = useState(LAYOUT_TYPE.DEFAULT);
  const [courseData, setCourseData] = useState(/**@type {?CourseData=}*/ (null));
  // if userProfile is passed as a prop, we will set it in state
  const [userProfileData, setUserProfileData] = useState(userProfile);
  const hasClassroomSession = classroomCourseId !== null;
  const [isConcurrent, setIsConcurrent] = useState(false);
  const [selectedCurrentLearningMode, setSelectedCurrentLearningMode] = useState(
    /**@type {LearningMode}*/ (LEARNING_MODE.DEFAULT)
  );
  const [isUnauthorizedUser, setIsUnauthorizedUser] = useState(false);
  const { enablePushNotification, enablePersonalizedNotification } = native ?? {};

  const { data } = useMyExternalProfile({ fetchPolicy: 'cache-only' });

  /** @param {LearningMode} learningMode */
  const setCurrentLearningMode = (learningMode) => {
    setSelectedCurrentLearningMode(learningMode);
    updateClassroomCurrentLearningMode({
      variables: {
        classroomId: classroomCourseId,
        learningModeId: learningMode,
      },
    });
  };

  const currentLearningMode = isEnableLearningMode
    ? selectedCurrentLearningMode
    : LEARNING_MODE.DEFAULT;

  const updateClassroomCurrentSubSectionCallback = useCallback((params) => {
    updateClassroomCurrentSubSection(params)
      .then(({ data }) => {
        // ANY-3349: gateway fail envelopes carry machine codes ('FORBIDDEN'), keep numeric for the not-yet-cut-over gateway
        if (['403', 'FORBIDDEN'].includes(data.updateClassroomCurrentSubSection.code)) {
          setIsConcurrent(true);
        }
      })
      .catch((error) => {
        const { graphQLErrors } = error || {};
        if (getError(graphQLErrors, '403')) {
          setIsConcurrent(true);
        }
      });
  }, []);

  const handleSetUnauthorizedUser = (isUnauthorizedUserArg) => {
    setIsUnauthorizedUser(isUnauthorizedUserArg);
  };

  const logoutCallback = useCallback(async () => {
    const { openUrl, isNative } = capacitorContext;
    const providerName = data?.myExternalProfile?.providerName ?? '';
    const classroomProviderData = getClassroomProviderData(providerName);
    const { hasExternalLogout = false, externalLogoutUrl = '' } = classroomProviderData ?? {};

    if (isNative && enablePushNotification && enablePersonalizedNotification) {
      try {
        const { token } = await FCMPlugin.getToken();
        const storeApolloClient = getStoreApolloClient();
        await storeApolloClient.mutate({
          mutation: nsUnregisterFcmToken,
          variables: { token },
        });
        log.debug('FCMToken unregistered successfully');
      } catch (error) {
        log.error('FCMToken unregistered failed: ', error);
      }
    }

    if (hasExternalLogout) {
      openUrl(externalLogoutUrl, () => {
        window.location.href = externalLogoutUrl;
      });
    } else {
      window.location.href = '/';
    }
  }, []);

  useEffect(() => {
    if (hasClassroomSession) {
      const updateCurrentSubSection = () => {
        updateClassroomCurrentSubSectionCallback({
          variables: {
            classroomId: classroomCourseId,
            subSectionReferId: currentSubsectionReferId,
          },
        });
      };
      updateCurrentSubSection();
    }
  }, [
    classroomCourseId,
    currentSubsectionReferId,
    hasClassroomSession,
    updateClassroomCurrentSubSectionCallback,
  ]);

  const initClassroom = async () => {
    if (!hasClassroomSession) {
      if (courseId) {
        const { data = {} } = await client.mutate({
          mutation: enterClassroom,
          variables: { courseId: courseId },
        });
        const enterClassroomData = data.enterClassroom || {};
        const { code, classroomInfo = {} } = enterClassroomData;
        if (code === '200') {
          const classroomCourseId = classroomInfo.classroomId;
          const classroomTimeId = classroomInfo.classroomTimeId;
          const courseData = await getCourseData(classroomCourseId, classroomTimeId);
          const inProgressSubsection = courseData?.inProgressSubsection;
          const courseCurrentLearningMode = courseData?.courseCurrentLearningMode ?? null;
          if (inProgressSubsection?.id !== currentSubsectionId) {
            updateCurrentSubsection(inProgressSubsection?.id, inProgressSubsection?.referId);
          }
          setSelectedCurrentLearningMode(courseCurrentLearningMode);
          setCourseData(courseData);
          setClassroomCourseId(classroomCourseId);
          setClassroomTimeId(classroomTimeId);
          return { code, classroomCourseId, classroomTimeId };
        }
        return enterClassroomData;
      } else {
        throw new Error(`Can't init classroom, No courseId Provided in ClassroomProvider`);
      }
    }
  };

  const getCourseData = async (
    classroomCourseIdArg = classroomCourseId,
    classroomTimeIdArg = classroomTimeId
  ) => {
    if (classroomCourseIdArg === null) return;

    const { data: classroomCourseData } = await client.query({
      query: classroomCourse,
      variables: {
        classroomCourseId: classroomCourseIdArg,
        classroomTimeId: classroomTimeIdArg,
        includeClassroomTime: classroomTimeIdArg ? true : false,
      },
      fetchPolicy: 'network-only',
    });
    const courseRevision = classroomCourseData?.classroomCourse?.revision;
    const courseTitle = classroomCourseData?.classroomCourse?.title;
    const courseSourceType = classroomCourseData?.classroomCourse?.sourceType;
    const courseClassroomMode = classroomCourseData?.classroomCourse?.classroomMode;
    const courseMode = classroomCourseData?.classroomCourse?.mode;
    const courseLearningModes = classroomCourseData?.classroomCourse?.learningModes ?? [
      LEARNING_MODE.DEFAULT,
    ];
    const courseLearningModeDetails = classroomCourseData?.classroomCourse?.learningModeDetails ?? [
      { id: LEARNING_MODE.DEFAULT, name: 'ทั่วไป', isActive: true },
    ];
    const courseCurrentLearningMode = classroomCourseData?.classroomCourse?.currentLearningMode;
    const isSectionGroupActive = getSectionGroupActiveStatus(
      classroomCourseData?.classroomCourse?.tableOfContentWithSectionGroup?.sectionGroups[0]
        ?.isVirtual
    );
    const tableOfContent = getToC(classroomCourseData, isSectionGroupActive);
    const sectionGroups = getSectionGroups(tableOfContent);
    const sections = getSections(tableOfContent, isSectionGroupActive);
    const subsections = getSubsectionsFromSections(sections);
    const completedSubsectionsIds = getCompletedSubsections(classroomCourseData);
    const failedSubsectionIds = getFailedSubsections(classroomCourseData);
    const learningModeCompletedSubsectionIds =
      getLearningModeCompletedSubsections(classroomCourseData);
    const submittedSubsectionIds = getSubmittedSubsections(classroomCourseData);
    const lastWatchingSubsectionReferId = getLastWatchingSubsectionReferId(classroomCourseData);
    const inProgressSubsection = getCurrentSubsection(
      subsections,
      lastWatchingSubsectionReferId,
      completedSubsectionsIds,
      subsectionReferId,
      targetSubsectionId
    );

    const sectionThemeColor = classroomCourseData.classroomCourse.themeColor;
    const partnerLogoUrl = classroomCourseData.classroomCourse.logoUrl;
    const tableOfContentTungkai = classroomCourseData.classroomCourse.tableOfContentTungkai;

    return {
      courseRevision,
      courseTitle,
      courseClassroomMode,
      courseSourceType,
      courseLearningModes,
      courseLearningModeDetails,
      courseCurrentLearningMode,
      isSectionGroupActive,
      sectionGroups,
      sections,
      subsections,
      completedSubsectionsIds,
      learningModeCompletedSubsectionIds,
      inProgressSubsection,
      sectionThemeColor,
      partnerLogoUrl,
      failedSubsectionIds,
      submittedSubsectionIds,
      courseMode,
      tableOfContentTungkai,
    };
  };

  const refreshCourseData = async () => {
    const courseData = await getCourseData();
    setCourseData(courseData);
  };

  /**
   * *** only for Resume Bookmark ***
   * isAutoPlayVideo for allow render <VideoBox/> ***not include quiz***
   * bookmarkProgress for specify which seconds to resume video in <VideoBox/>
   */
  const [isAutoPlayVideo, setIsAutoPlayVideo] = useState(true);
  const [bookmarkProgress, setBookmarkProgress] = useState(null);

  const getSectionGroupActiveStatus = (isVirtual) => {
    return !isVirtual;
  };

  const updateCurrentSubsection = (newId, newReferId) => {
    setCurrentSubsectionId(newId);
    setCurrentSubsectionReferId(newReferId);
  };

  const updateLayout = (layoutType) => {
    setLayoutType(layoutType);
  };

  const updateBookmarkProgress = (newBookmarkProgress) => {
    setBookmarkProgress(newBookmarkProgress);
  };

  const updateIsAutoPlayVideo = (isAutoPlayVideo) => {
    setIsAutoPlayVideo(isAutoPlayVideo);
  };

  const updateIsEnableVideoResume = (isEnabled) => {
    setIsEnableVideoResume(isEnabled);
  };

  const updateUserProfileData = (userProfile) => {
    setUserProfileData(userProfile);
  };

  const updateIsConcurrent = (isConcurrent) => {
    setIsConcurrent(isConcurrent);
  };

  const subsectionIdSourceMissionIdMapper = useMemo(() => {
    const mapper = new Map();
    if (courseData?.tableOfContentTungkai) {
      for (const section of courseData.tableOfContentTungkai.sections) {
        for (const mission of section.missions) {
          for (const subSection of mission.subSections) {
            mapper.set(subSection.id, mission.referId);
          }
        }
      }
    }
    return mapper;
  }, [courseData]);

  return (
    <ClassroomContext.Provider
      value={{
        refreshCourseData,
        currentSubsectionId,
        currentSubsectionReferId,
        updateCurrentSubsection,
        classroomCourseId,
        classroomTimeId,
        courseId,
        isEnableVideoResume,
        updateIsEnableVideoResume,
        layoutType,
        updateLayout,
        bookmarkProgress,
        updateBookmarkProgress,
        isAutoPlayVideo,
        updateIsAutoPlayVideo,
        courseData,
        initClassroom,
        hasClassroomSession,
        userProfileData,
        updateUserProfileData,
        isConcurrent,
        updateIsConcurrent,
        currentLearningMode,
        setCurrentLearningMode,
        isUnauthorizedUser,
        handleSetUnauthorizedUser,
        logoutCallback,
        subsectionIdSourceMissionIdMapper,
      }}
    >
      {children}
    </ClassroomContext.Provider>
  );
};

ClassroomProvider.defaultProps = {};

export default compose(
  graphql(updateClassroomCurrentSubSection, {
    name: 'updateClassroomCurrentSubSection',
  }),
  graphql(updateClassroomCurrentLearningMode, {
    name: 'updateClassroomCurrentLearningMode',
  }),
  withApollo,
  withConfig,
  withCapacitor
)(ClassroomProvider);

/**
 * @typedef {import("./context").CourseData} CourseData
 * @typedef {import("./constant").LearningMode} LearningMode
 *
 * @typedef {Record<string, LearningMode>} CoursesLearningMode
 */
